import { useId, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  ActionComments,
  ActionRowGlyph,
  ActionRowHeader,
  EditablePayload,
  ProposalPricebookMatchButton,
  PurchaseOrderMatchButton,
} from "@/accounts-payable/common";
import {
  isQboBillActionType,
  qboBillPayloadEdit,
} from "@/accounts-payable/quickbooks-bill";
import type { ApAgentAction, ApDocumentType } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  approveLabel,
  getNewItemsToCreate,
  hasEditablePayload,
  isApprovedWithPendingAction,
  isNoOpAction,
  isPendingReviewAction,
  purchaseOrderProposalLink,
} from "@/accounts-payable/utils";
import { NewPricebookItemsNote } from "./new-pricebook-items-note";
import { RECORD_CARD_CLASS } from "./record-fields";
import { TargetObject } from "./target-object";

// A 36px touch target on a phone; the compact pill from sm up.
const TOUCH_PILL = "h-9 sm:h-8";

/**
 * One agent action on the review page: the sidebar's row header (status glyph,
 * category, confidence, outcome), then the reasoning, the ServiceTitan record
 * in a grey card, comments, and the decision as labelled pills. Pending rows
 * leave the target to the record card so it isn't said twice. A PO carrying
 * new pricebook items says so and names the extra items its approval creates.
 */
export const ActionReviewCard = ({
  action,
  runActions,
  businessId,
  document,
  onResolve,
  onSave,
  resolving,
  saving,
}: {
  action: ApAgentAction;
  /** Every action of the action's run, to find the new items a PO carries. */
  runActions: ApAgentAction[];
  businessId: string | null;
  /**
   * The document the action is for, and the run it came from. With it, a PO
   * or pricebook proposal offers choosing the PO or the line's item by hand
   * instead.
   */
  document?: {
    id: string;
    type: ApDocumentType;
    agentRunId: string | null;
    /** Whether the document has a job, whose POs the PO picker opens on. */
    hasJob: boolean;
  };
  onResolve: (decision: "approved" | "rejected") => void;
  /** `onSaved` closes the editor, so a refused save keeps the draft. */
  onSave: (payload: ApAgentAction["payload"], onSaved: () => void) => void;
  resolving?: boolean;
  saving?: boolean;
}) => {
  const pending = isPendingReviewAction(action);
  const noOp = isNoOpAction(action);
  // Every card has the same three buttons, so each one's name says which
  // action it decides ("Approve: Lower cost").
  const subject = noOp ? actionDetail(action) : actionCategoryLabel(action);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ApAgentAction["payload"]>(action.payload);

  const startEdit = () => {
    setDraft({ ...action.payload });
    setEditing(true);
  };
  const save = () => {
    const close = () => setEditing(false);
    if (!isQboBillActionType(action.action_type)) {
      onSave(draft, close);
      return;
    }
    // A bill sends only what the API lets a reviewer change, and only what
    // changed; the API refuses an empty edit, so nothing changed just closes.
    const edit = qboBillPayloadEdit(draft, action.payload);
    if (Object.keys(edit).length === 0) close();
    else onSave(edit, close);
  };
  const disabled = resolving || saving;
  // Whether the API would refuse to approve, and the new pricebook items the
  // approval creates: both straight from the API, so the card shows what it
  // enforces. A blocked PO creates nothing, so it lists no items.
  const blockedReason = pending ? action.approval_blocked_reason : null;
  const approveBlocked = Boolean(blockedReason);
  const newItems =
    pending && !approveBlocked ? getNewItemsToCreate(action, runActions) : [];
  // Ties the disabled Approve button to the reason shown in the note.
  const blockedReasonId = useId();
  const poLink = document
    ? purchaseOrderProposalLink(action, document.hasJob)
    : null;

  const controls = pending ? (
    editing ? (
      <Stack direction="row" gap={1} align="center">
        <Button
          size="sm"
          className={TOUCH_PILL}
          variant="secondary"
          onClick={save}
          disabled={disabled || saving}
          loading={saving}
        >
          Save changes
        </Button>
        <Button
          size="sm"
          className={TOUCH_PILL}
          variant="ghost"
          disabled={disabled}
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </Stack>
    ) : (
      <Stack direction="row" gap={1} align="center" className="flex-wrap">
        {/* Labelled pills, one size: the primary in green, the rest grey. On a
            flag, approving records it seen and rejecting records it ignored. */}
        <Button
          size="sm"
          className={TOUCH_PILL}
          variant={noOp ? "outline" : "secondary"}
          onClick={() => onResolve("approved")}
          disabled={disabled || approveBlocked}
          aria-label={`${noOp ? "Mark as seen" : approveLabel(action, runActions)}: ${subject}`}
          aria-describedby={approveBlocked ? blockedReasonId : undefined}
        >
          {noOp ? "Mark as seen" : approveLabel(action, runActions)}
        </Button>
        {hasEditablePayload(action) && (
          <Button
            size="sm"
            className={TOUCH_PILL}
            variant="outline"
            onClick={startEdit}
            disabled={disabled}
            aria-label={`Edit: ${subject}`}
          >
            Edit
          </Button>
        )}
        <Button
          size="sm"
          className={TOUCH_PILL}
          variant="outline"
          onClick={() => onResolve("rejected")}
          disabled={disabled}
          aria-label={`${noOp ? "Ignore" : "Dismiss"}: ${subject}`}
        >
          {noOp ? "Ignore" : "Dismiss"}
        </Button>
        {/* The PO is wrong rather than the proposal's details: choosing it
            supersedes this proposal and has the agent re-check. */}
        {document && poLink && (
          <PurchaseOrderMatchButton
            businessId={businessId}
            documentId={document.id}
            documentType={document.type}
            agentRunId={document.agentRunId}
            hasJob={poLink.hasJob}
            label={poLink.label}
            variant="outline"
          />
        )}
        {/* Likewise when the proposal is aimed at the wrong pricebook item:
            choosing the line's item supersedes it. */}
        {document && (
          <ProposalPricebookMatchButton
            businessId={businessId}
            documentId={document.id}
            documentType={document.type}
            agentRunId={document.agentRunId}
            action={action}
          />
        )}
      </Stack>
    )
  ) : null;

  return (
    <Stack direction="row" gap={2} align="start" className="w-full p-4">
      <ActionRowGlyph action={action} />
      <Stack align="start" gap={4} className="min-w-0 flex-1">
        <ActionRowHeader action={action} showTarget={!pending} />

        {/* Why, before what: the reasoning leads, the record follows. */}
        {pending &&
          action.rationale &&
          action.rationale !== actionDetail(action) && (
            <Text tone="muted">{action.rationale}</Text>
          )}

        {pending &&
          (editing ? (
            <Stack
              align="start"
              gap={1}
              className={cn("w-full", RECORD_CARD_CLASS, "text-sm")}
            >
              <Text variant="caption" tone="muted">
                Editing
              </Text>
              <EditablePayload
                action={action}
                businessId={businessId}
                draft={draft}
                setDraft={setDraft}
              />
            </Stack>
          ) : (
            <TargetObject action={action} />
          ))}

        {(newItems.length > 0 || blockedReason) && !editing && (
          <NewPricebookItemsNote
            items={newItems}
            blockedReason={blockedReason}
            blockedReasonId={blockedReasonId}
          />
        )}

        {pending && isApprovedWithPendingAction(action, runActions) && (
          <Text variant="caption" tone="muted">
            This item goes on the purchase order in this run. Approving that PO
            approves this item too.
          </Text>
        )}

        {/* A note is part of reaching the decision, so it sits above it; the
            decision closes the card. */}
        <ActionComments action={action} businessId={businessId} />
        {controls}
      </Stack>
    </Stack>
  );
};
